import { useEffect, useRef, useState } from 'react';
import TransactionDetailModal from '../components/TransactionDetailModal';
import { getTransactions } from '../services/paymentService';

const PAGE_SIZE = 10;

const STATUS_DISPLAY = {
  completed: { label: 'Success', className: 'success' },
  pending: { label: 'Pending', className: 'pending' },
  failed: { label: 'Failed', className: 'failed' },
  refunded: { label: 'Refunded', className: 'refunded' },
};

const PLATFORM_LABELS = {
  ios: 'iOS',
  android: 'Android',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatAmount(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amount));
}

function getStatusDisplay(status) {
  const key = String(status || '').toLowerCase();
  return STATUS_DISPLAY[key] || {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
    className: key || 'pending',
  };
}

function packageLabel(txn) {
  const petals = txn.package?.petalCount ?? txn.petalCount;
  if (petals == null) return 'Petal Pack';
  return petals >= 1000 ? 'Premium Pack' : 'Basic Pack';
}

function getPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = [1];

  if (current > 3) items.push('…');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    items.push(pageNum);
  }

  if (current < total - 2) items.push('…');

  items.push(total);
  return items;
}

export default function Payments() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewPurchaseId, setViewPurchaseId] = useState(null);

  const scrollRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    async function loadTransactions() {
      setLoading(true);
      setError('');

      try {
        const response = await getTransactions({
          page,
          limit: PAGE_SIZE,
          status: statusFilter || undefined,
          platform: platformFilter || undefined,
          search: debouncedSearch || undefined,
        });

        if (cancelled) return;

        setTransactions(Array.isArray(response.data) ? response.data : []);
        setTotalPages(response.pagination?.totalPages || 1);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to fetch transactions.');
        setTransactions([]);
        setTotalPages(1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTransactions();
    return () => {
      cancelled = true;
    };
  }, [page, statusFilter, platformFilter, debouncedSearch]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [page]);

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page || loading) return;
    setPage(nextPage);
  }

  const pageItems = getPageItems(page, totalPages);

  return (
    <div className="payments-page main-bg-top">
      <div className="users-header">
        <h2>All Transactions</h2>
        <div className="users-filters">
          <div className="users-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search by transaction ID or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="completed">Success</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={platformFilter}
              onChange={(e) => {
                setPlatformFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Platforms</option>
              <option value="ios">iOS</option>
              <option value="android">Android</option>
            </select>
          </div>
        </div>
      </div>

      <div className="payments-table-scroll" ref={scrollRef}>
        <div className="users-table-wrap">
          <table className="users-table payments-table">
            <thead>
              <tr>
                <th>Transactions ID</th>
                <th>User</th>
                <th>Package</th>
                <th>Petals</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Status</th>
                <th>Date & Time</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="users-empty">Loading transactions...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="9" className="users-empty">{error}</td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan="9" className="users-empty">No transactions found.</td>
                </tr>
              ) : (
                transactions.map((txn) => {
                  const status = getStatusDisplay(txn.status);
                  const name = txn.user?.name || '—';
                  const email = txn.user?.email || '';
                  const packagePetals = txn.package?.petalCount ?? txn.petalCount ?? 0;
                  const platform = PLATFORM_LABELS[txn.platform] || txn.platform || '—';

                  return (
                    <tr key={txn.id}>
                      <td className="txn-id-cell" data-label="Transactions ID">{txn.transactionId || '—'}</td>
                      <td data-label="User">
                        <div className="user-cell">
                          <div className="user-cell-avatar">
                            {txn.user?.image?.url ? (
                              <img
                                src={txn.user.image.url}
                                alt={name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.textContent = name !== '—' ? name.charAt(0).toUpperCase() : '?';
                                }}
                              />
                            ) : (
                              <span className="avatar-placeholder">
                                {name !== '—' ? name.charAt(0).toUpperCase() : '?'}
                              </span>
                            )}
                          </div>
                          <div className="user-cell-info">
                            <span className="user-cell-name">{name}</span>
                            <span className="user-cell-email">{email}</span>
                          </div>
                        </div>
                      </td>
                      <td data-label="Package">
                        <div className="package-cell">
                          <span className="package-name">
                            {packageLabel(txn)}{' '}
                            <img
                              alt=""
                              className="menu-icon-img"
                              src={`${process.env.PUBLIC_URL}/images/star.svg`}
                            />
                          </span>
                          <span className="package-petals">{packagePetals} Petals</span>
                        </div>
                      </td>
                      <td data-label="Petals">{txn.petalCount ?? '—'}</td>
                      <td data-label="Amount">{formatAmount(txn.price)}</td>
                      <td data-label="Payment Method">{platform}</td>
                      <td data-label="Status">
                        <span className={`status-badge status-${status.className}`}>{status.label}</span>
                      </td>
                      <td data-label="Date & Time">
                        <div className="datetime-cell">
                          <span className="datetime-date">{formatDate(txn.createdAt)}</span>
                          <span className="datetime-time">{formatTime(txn.createdAt)}</span>
                        </div>
                      </td>
                      <td data-label="Actions">
                        <div className="actions-cell">
                          <button
                            className="action-btn"
                            title="View"
                            type="button"
                            onClick={() => setViewPurchaseId(txn.id)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className="payments-pagination" aria-label="Pagination">
          <button
            type="button"
            className="pagination-page-btn"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>

          {pageItems.map((item, index) =>
            item === '…' ? (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`pagination-page-btn ${page === item ? 'active' : ''}`}
                onClick={() => goToPage(item)}
                aria-current={page === item ? 'page' : undefined}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            className="pagination-page-btn"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}

      <TransactionDetailModal
        open={Boolean(viewPurchaseId)}
        purchaseId={viewPurchaseId}
        onClose={() => setViewPurchaseId(null)}
      />
    </div>
  );
}
